import React, { PropsWithChildren } from 'react';
import { useProjectId, useSelectedProjectInitializer } from 'store/project';
import { useSitesInitializer } from 'store/site';
import { useLevelsInitializer, useLevelsToSelectInitializer } from 'store/level';
import { useCompaniesInitializer } from 'store/company';
import { useContractsInitializer } from 'store/contract';
import { useFieldValuesLifecycle } from 'store/fieldValue';
import { useIssuesInitializer } from 'store/issue';
import { useWorkflowsInitializer } from 'store/workflow';
import { useProcessesInitializer } from 'store/process';
import { useIssueFormInitializer } from 'store/issueForm';
import { useVisibleFieldsInitializer } from 'store/visibleFields';
import { useInspectionsInitializer } from 'store/inspection';
import { useProjectUsersInitializer } from 'store/projectUsers';
import { useOrganizationsInitializer } from 'store/organizations';
import { useProjectsInitializer } from 'store/projects';

/** Stores needed before a project is selected (e.g. on the project selection view). */
function SessionStoresInitializers(): null {
  useSelectedProjectInitializer();
  useOrganizationsInitializer();
  useProjectsInitializer();

  return null;
}

function EntityStoresInitializers(): null {
  useSitesInitializer();
  useLevelsInitializer();
  useLevelsToSelectInitializer();
  useCompaniesInitializer();
  useContractsInitializer();
  useFieldValuesLifecycle();
  useIssuesInitializer();
  useWorkflowsInitializer();
  useProcessesInitializer();
  useIssueFormInitializer();
  useVisibleFieldsInitializer();
  useInspectionsInitializer();
  useProjectUsersInitializer();

  return null;
}

/**
 * Project-scoped stores start fetching only once a project is selected: before
 * that (project selection view) every getAll would wait for a projectId that
 * never comes and end in a timeout. Session stores (selected project, organizations, projects) start at once.
 */
export function WithEntityStoresInitializer({
  children,
}: PropsWithChildren): React.ReactElement {
  const projectId = useProjectId();

  return (
    <>
      <SessionStoresInitializers />
      {projectId && <EntityStoresInitializers />}
      {children}
    </>
  );
}
